import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { useRoleNotActiveStore } from "@/features/auth/store/role-not-active.store";
import { registerRoleNotActivePrompt } from "@/lib/api-client";
import { RoleSwitchPrompt } from "./role-switch-prompt";

/**
 * After a request is refused with 403 `ROLE_NOT_ACTIVE`, offers to switch to
 * the role it needed. Hidden once that role is active or the prompt is
 * dismissed. Mounted once, by the app layout.
 */
export function RoleNotActivePrompt(): React.JSX.Element | null {
	const { t } = useTranslation();
	const role = useRoleNotActiveStore((state) => state.role);
	const clear = useRoleNotActiveStore((state) => state.clear);
	const { user } = useAuthStore();
	const activeRole = user?.userType ?? null;

	// While this is mounted, the API client leaves the generic error toast
	// out for a ROLE_NOT_ACTIVE refusal, which this prompt shows instead.
	useEffect(() => registerRoleNotActivePrompt(), []);

	if (!role || role === activeRole) return null;

	return (
		<div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-xl rounded-xl shadow-[var(--dashboard-shadow-3)]">
			<RoleSwitchPrompt
				role={role}
				className="border-dashboard-border bg-dashboard-paper"
				onDismiss={clear}
				message={t("roleSwitcher.roleNotActive", {
					role: t(`roleSession.roleNames.${role}`),
				})}
			/>
		</div>
	);
}
